import { Col, Row } from "@amzn/stencil-react-components/layout";
import { SelectProps } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label } from "@amzn/stencil-react-components/text";
import { TOOLTIP_POSITION, withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconInformationFill } from "@amzn/stencil-react-icons";
import { Checkbox as UXMCheckbox } from "@amzn/uxm-stencil-react-components/lib/src/stencil/checkbox";
import { isArray } from "lodash";
import React, { FC, ReactNode } from "react";

import categoriesOverride from "../../../configuration/AIForMe/categoriesOverride.json";

const IconInformationFillWithTooltip = withTooltip()(IconInformationFill);

export type MultiSelectProps = Omit<SelectProps<string>, "onChange"> & {
  onChange: (value: string[]) => void;
  showToolTip?: (ReactNode | undefined)[];
  columns: number;
  touchpoint: string;
  isLoading?: boolean;
};

export const MultiSelect: FC<MultiSelectProps> = (props: MultiSelectProps) => {
  let values: string[] = [];
  if (props.value) {
    if (!isArray(props.value)) {
      values = [props.value];
    } else {
      values = props.value;
    }
  }

  const showToolTip = props.showToolTip || props.options.map(() => undefined);

  if (props.isLoading) {
    return <Spinner size={SpinnerSize.Small}></Spinner>;
  }

  const options = props.options.map((option: string, index: number) => {
    const checked = values.includes(option);

    // This is a temporary measure to change the display name of a category
    // This will require a refactor on Catalogical side and this was required as urgent
    const getOptionDisplayName = (option: string) =>
      (categoriesOverride as { [key: string]: string })[option] ?? option;

    return (
      <Row alignItems="center" gridGap="S200" flex={1} key={option}>
        <UXMCheckbox
          id={option}
          value={option}
          checked={checked}
          data-testid="multiselect-option"
          onChange={(e: { target: { checked: any; value: string } }) => {
            if (props.onChange) {
              if (e.target.checked) {
                props.onChange([...values, e.target.value]);
              } else {
                props.onChange(values.filter((v: string) => v !== e.target.value));
              }
            }
          }}
          touchpoint={props.touchpoint}
        />
        <Label htmlFor={option}>{getOptionDisplayName(option)}</Label>
        {!!showToolTip[index] && (
          <IconInformationFillWithTooltip
            title="Show tooltip"
            tooltipText={showToolTip[index]}
            tooltipPosition={TOOLTIP_POSITION.BOTTOM}
          />
        )}
      </Row>
    );
  });

  function renderRows(options: React.JSX.Element[], columns: number): React.ReactNode[] {
    const rows: React.ReactNode[] = [];
    for (let i = 0; i < options.length; i += columns) {
      const rowOptions = options.slice(i, i + columns);
      if (rowOptions.length < columns) {
        for (let j = rowOptions.length; j < columns; j++) {
          rowOptions.push(<Col flex={1} key={`empty-${i}-${j}`} />);
        }
      }
      rows.push(
        <Row gridGap="S200" key={i}>
          {rowOptions}
        </Row>
      );
    }
    return rows;
  }

  return (
    <Col rowGap="5px" {...props} onChange={() => {}}>
      {renderRows(options, props.columns)}
    </Col>
  );
};
